import React from "react"
import { MdEdit } from "react-icons/md"
import { useMutation } from "react-query"
import { matchSorter } from "match-sorter"
import { Box, Flex, Tag as ChakraTag, Text, Button, IconButton } from "@chakra-ui/react"
import { BiCheck } from "react-icons/bi"

import { TagItem } from "./TagItem"
import { OrgEntity } from "../lib/types/org"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { useOrgs } from "./providers/OrgProvider"
import { TagEntity, TagDeclaration, TagGroupEntity, AssignedTagDeclaration } from "../lib/types/customTag"
import { isSupervisor } from "../lib/helpers/role"
import { useMe } from "./providers/MeProvider"

interface Props {
  tagName: string
  tagColor: string
  tagGroups: TagGroupEntity[]
  createTagInstanceIsLoading: boolean
  assignedTags: AssignedTagDeclaration[]
  tags: TagEntity[]
  onTagAssign: (uuid: string) => void
  onTagCreate: (name: string, color: string) => void
  onTagDelete: () => void
  onTagUpdate: () => void
  selectedGroup?: TagGroupEntity
  selectedOrg?: OrgEntity
  onGroupSelected: (tagGroup: TagGroupEntity) => void
  onOrgSelected: (org: OrgEntity) => void
  isCreatingTag: boolean
}

export const CustomTagsList = ({
  tagName,
  tagColor,
  tagGroups,
  tags,
  assignedTags,
  onTagAssign,
  onTagCreate,
  onTagDelete,
  onTagUpdate,
  selectedGroup,
  selectedOrg,
  onGroupSelected,
  onOrgSelected,
  isCreatingTag,
}: Props) => {
  const toast = useToast()
  const me = useMe()
  const { currentOrgs } = useOrgs()

  const [isSelectingGroup, setIsSelectingGroup] = React.useState<boolean>(false)
  const [isSelectingOrg, setIsSelectingOrg] = React.useState<boolean>(false)

  const { mutateAsync: deleteTag } = useMutation<TagEntity, unknown, string>((uuid) =>
    fetcher(`custom-tagging/tag_declarations/${uuid}/`, { method: "delete" }),
  )

  const { mutateAsync: updateTag } = useMutation<TagEntity, unknown, TagEntity>((tag) =>
    fetcher(`custom-tagging/tag_declarations/${tag.uuid}/`, { body: JSON.stringify(tag), method: "put" }),
  )
  const updateTagInfo = async (tag: TagEntity) => {
    try {
      await updateTag(tag)
      onTagUpdate()
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleDeleteTag = async (uuid: string) => {
    try {
      await deleteTag(uuid)
      onTagDelete()
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleAssignTag = (id: string) => {
    const tag = tags.find((t) => t.uuid === id)
    if (!tag || assignedTags.find((t) => t.tag_declaration_name === tag.name)) return
    onTagAssign(tag.uuid)
  }

  const handleUpdateColor = (id: string, color: string) => {
    const existingTag = tags.find((t) => t.uuid === id)
    if (!existingTag) return
    updateTagInfo({ ...existingTag, color: color })
  }

  const handleUpdateName = (id: string, name?: string) => {
    if (!name?.trim() || tags.find((t) => t.name === name)) return
    const existingTag = tags.find((t) => t.uuid === id)
    if (!existingTag) return
    updateTagInfo({ ...existingTag, name: name.trim() })
  }

  const isTagSelected = (tag: TagDeclaration) => {
    return assignedTags.find((assignedTag) => assignedTag.uuid === tag.uuid)
  }

  const matchedTags = (tags: TagDeclaration[]) => {
    return matchSorter(tags, tagName, { keys: ["name"] })
  }

  return (
    <Box w="100%" border="1px solid" borderColor="gray.100" bg="white" pb={2} maxH="270px" overflowY="scroll">
      <Text color="gray.500" fontSize="0.8rem" px={3} pt={3} pb={1}>
        {isSelectingGroup
          ? "Select tag group"
          : isSelectingOrg
          ? "Select organization"
          : isSupervisor(me.roles)
          ? "Select an option or create one"
          : "Select an option"}
      </Text>
      <Flex direction="column">
        {!isSelectingGroup &&
          !isSelectingOrg &&
          tagGroups.map(({ name, declarations }) => (
            <Box key={name}>
              {matchedTags(declarations).length > 0 && (
                <Text fontSize="0.8rem" fontWeight={550} mb={1} px={3}>
                  {name}
                </Text>
              )}
              {matchedTags(declarations).map((tag) => {
                if (isTagSelected(tag)) return null
                return (
                  <TagItem
                    key={tag.uuid}
                    tag={tag}
                    active={!isCreatingTag && matchedTags(declarations).length === 1}
                    handleAssign={handleAssignTag}
                    handleDelete={handleDeleteTag}
                    handleUpdateColor={handleUpdateColor}
                    handleUpdateName={handleUpdateName}
                  />
                )
              })}
            </Box>
          ))}
        {isCreatingTag && !isSelectingGroup && !isSelectingOrg && (
          <Flex align="center" justify="space-between" px={3} py={1} bg="gray.50">
            <Flex align="center" w="100%">
              <Text fontSize="0.9rem">Create</Text>
              <ChakraTag ml={1} bg={tagColor} size="sm">
                {tagName}
              </ChakraTag>
              <Text fontSize="0.9rem" ml={1}>
                in
              </Text>
              <Button
                size="xs"
                ml={1}
                variant="outline"
                colorScheme={selectedGroup ? "green" : undefined}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsSelectingGroup(!isSelectingGroup)
                }}
              >
                <Flex align="center">
                  <Text mr={1} maxW="70px">
                    {selectedGroup ? selectedGroup.name : "group"}
                  </Text>
                  <MdEdit />
                </Flex>
              </Button>
              {currentOrgs.length > 1 && (
                <>
                  <Text fontSize="0.9rem" ml={1}>
                    at
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme={selectedOrg ? "green" : undefined}
                    ml={1}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsSelectingOrg(!isSelectingOrg)
                    }}
                  >
                    <Flex align="center">
                      <Text mr={2} maxW="70px" isTruncated={!!selectedOrg}>
                        {selectedOrg ? selectedOrg.name : "organization"}
                      </Text>
                      <MdEdit />
                    </Flex>
                  </Button>
                </>
              )}
            </Flex>
            <IconButton
              aria-label="create tag"
              as={BiCheck}
              cursor="pointer"
              size="xs"
              minW="45px"
              colorScheme="green"
              onClick={() => {
                if (!selectedGroup) return setIsSelectingGroup(true)
                if (!selectedOrg && currentOrgs.length > 1) return setIsSelectingOrg(true)
                onTagCreate(tagName, tagColor)
              }}
            />
          </Flex>
        )}
        {isSelectingGroup &&
          tagGroups.map((tagGroup) => (
            <Button
              key={tagGroup.uuid}
              variant="unstyled"
              alignItems="center"
              borderRadius={0}
              textAlign="left"
              justifyContent="flex-start"
              mb={1}
              size="sm"
              fontWeight={400}
              pl={3}
              py={1}
              _hover={{ bg: "gray.50" }}
              onClick={() => {
                onGroupSelected(tagGroup)
                setIsSelectingGroup(false)
              }}
            >
              <Text pl={4}>{tagGroup.name}</Text>
            </Button>
          ))}
        {isSelectingOrg &&
          currentOrgs.map((org) => (
            <Button
              key={org.uuid}
              variant="unstyled"
              alignItems="center"
              borderRadius={0}
              textAlign="left"
              justifyContent="flex-start"
              mb={1}
              size="sm"
              fontWeight={400}
              pl={3}
              py={1}
              _hover={{ bg: "gray.50" }}
              onClick={() => {
                onOrgSelected(org)
                setIsSelectingOrg(false)
              }}
            >
              <Text pl={4}>{org.name}</Text>
            </Button>
          ))}
      </Flex>
    </Box>
  )
}
